// Hooks
import React, { useCallback, useEffect, useState, useRef } from "react"; // Import useRef

// Components
import TrackTask from "./track-task/trackTask";
import InputDropdown from "../../../Shared/input-dropdown/inputDropdown";
import { buttonStyles, StyledButton } from "../../../Shared/button/button";
import AddBatchModal from "./add-batch";
import Loading from "../../../Shared/loading/loading";
import { ReactComponent as RefreshSvg } from "../../../assets/images/refresh-cw.svg";

// styles
import styles from "./batch-service.module.scss";
import { ACCENTCOLOR, SECONDARYCOLOR } from "../../../Shared/colors";

// Functions
import { batchPaths } from "../../../api/batch";
import { callApi, callApiEnvelope } from "../../../api/fetch";
import {
  batchTasksHasMore,
  batchTasksListSearchParams,
  extractTaskListPayload,
  extractTaskListTotal,
} from "./batchApiMappers";
import { normalizeCustomerBatchTasks, matchesCustomerBatchFilter } from "./normalizeBatchTask";

// Constants
import ArrowDownSVG from "../../../../src/components/application/developer-docs/svg/arrowDownSVG";
import ArrowUpSVG from "../../../../src/components/application/developer-docs/svg/arrowUpSVG";

import Toast from "../../../Shared/toast/toast";
import CustomDatePicker from "../../../Shared/custom-date-picker/customDatePicker";
import { format } from "date-fns";

import WarningBar from "../dashboard/warning-bar/warningBar";
import Input from "../../../Shared/input/input";
import { IdsGenerator } from "../../../utils/idGenerator";

const dropdownData = [
  { label: "Completed", value: "FINISHED" },
  { label: "In Progress", value: "PROGRESS" },
  { label: "Paused", value: "PAUSED" },
  { label: "Error", value: "ERROR" },
  { label: "Waiting for approval", value: "Waiting for approval" },
];

// Cache configuration
const BATCH_TYPES_CACHE_KEY = "batchTypes_cache";
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes
const BACKGROUND_REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

// Cache utility functions
const getCachedBatchTypes = (organisationId: string | null) => {
  if (!organisationId) return null;
  try {
    const cacheKey = `${BATCH_TYPES_CACHE_KEY}_${organisationId}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_DURATION) {
        return data;
      }
    }
  } catch (error) {
    // No console.error here as per instruction
  }
  return null;
};

const setCachedBatchTypes = (organisationId: string | null, data: any[]) => {
  if (!organisationId) return;
  try {
    const cacheKey = `${BATCH_TYPES_CACHE_KEY}_${organisationId}`;
    const cacheData = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  } catch (error) {
    // No console.error here as per instruction
  }
};

const BatchService = () => {
  const organisationId = localStorage.getItem("organisation_id");
  const [search, setSearch] = useState<string>("");
  const [addBatch, setAddBatch] = useState<boolean>(false);
  const [batchTypes, setBatchTypes] = useState<any[]>([]);
  const [taskList, setTaskList] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [filterTask, setFilterTask] = useState<any[]>([]);
  const [resetProductDropdown, setResetProductDropdown] = useState(false);
  const [resetStatusDropdown, setResetStatusDropdown] = useState(false);
  const [isBatchTypesLoading, setIsBatchTypesLoading] = useState<boolean>(true);
  const [showCustomDatePicker, setShowCustomDatePicker] =
    useState<boolean>(false);
  const backgroundRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const nextApiPageRef = useRef(1);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [state, setState] = useState<any>(null);
  const [pendingFilters, setPendingFilters] = useState<any>(null);
  const [downTime, setDownTime] = useState<boolean>(false);
  const [customDurationPlaceholder, setCustomDurationPlaceholder] = useState<
    string | null
  >(null);

  const fetchBatchTypes = useCallback(
    async (forceRefresh = false, showLoading = true) => {
      if (!organisationId) {
        setIsBatchTypesLoading(false);
        setBatchTypes([]);
        return;
      }

      try {
        if (!forceRefresh) {
          const cachedData = getCachedBatchTypes(organisationId);
          if (cachedData) {
            setBatchTypes(cachedData);
            setIsBatchTypesLoading(false);

            // Clear existing timer and set a new one for background refresh
            if (backgroundRefreshTimerRef.current) {
              clearTimeout(backgroundRefreshTimerRef.current);
            }
            backgroundRefreshTimerRef.current = setTimeout(() => {
              fetchBatchTypes(true, false);
            }, BACKGROUND_REFRESH_INTERVAL);
            return;
          }
        }

        if (showLoading) {
          setIsBatchTypesLoading(true);
        }

        const data = await callApi(batchPaths.types, {
          method: "GET",
        });

        if (data?.length) {
          setBatchTypes(data);
          setCachedBatchTypes(organisationId, data);
        } else {
          setBatchTypes([]);
          setCachedBatchTypes(organisationId, []);
        }

        // Clear existing timer and set a new one for background refresh
        if (backgroundRefreshTimerRef.current) {
          clearTimeout(backgroundRefreshTimerRef.current);
        }
        backgroundRefreshTimerRef.current = setTimeout(() => {
          fetchBatchTypes(true, false);
        }, BACKGROUND_REFRESH_INTERVAL);
      } catch (err) {
        const cachedData = getCachedBatchTypes(organisationId);
        if (cachedData) {
          setBatchTypes(cachedData);
        } else {
          setBatchTypes([]);
        }
      } finally {
        if (showLoading) {
          setIsBatchTypesLoading(false);
        }
      }
    },
    [organisationId]
  );

  useEffect(() => {
    fetchBatchTypes(false, true);

    return () => {
      // Clear the timeout when the component unmounts
      if (backgroundRefreshTimerRef.current) {
        clearTimeout(backgroundRefreshTimerRef.current);
      }
    };
  }, [fetchBatchTypes]); // Only fetchBatchTypes is needed here now

  const toastTimeout = useCallback(function toastTimeout(
    showToastValue: boolean,
    toastType: "success" | "error",
    toastMessage: string | string[],
    heading?: string
  ) {
    if (!showToastValue) return;

    // Show toast via react-toastify
    Toast(
      toastType,
      toastMessage,
      heading || (toastType === "success" ? "Success" : "Error")
    );
  }, []);

  const searchData = (items: any, searchParam: string) => {
    const data = items.filter((item: any) => {
      return (
        item.batchName &&
        item.batchName.toLowerCase().includes(searchParam.trim().toLowerCase())
      );
    });
    return data;
  };

  const filterTasksClientSide = useCallback(
    (items: any[], filtersToApply: any, searchParam: string) => {
      if (searchParam.trim()) {
        return searchData(items, searchParam);
      }
      if (!filtersToApply) {
        return items;
      }

      return items.filter((item: any) => {
        const itemDate = item?.created_at
          ? new Date(item.created_at.split("T")[0])
          : null;

        const productMatch =
          !filtersToApply?.batchType ||
          item.batchType === filtersToApply.batchType;

        const statusMatch =
          !filtersToApply?.value ||
          matchesCustomerBatchFilter(item, filtersToApply.value);

        let dateMatch = true;
        if (filtersToApply?.fromDate && filtersToApply?.toDate && itemDate) {
          const fromDate = new Date(filtersToApply.fromDate);
          const toDate = new Date(filtersToApply.toDate);
          dateMatch = itemDate >= fromDate && itemDate <= toDate;
        }

        return productMatch && statusMatch && dateMatch;
      });
    },
    []
  );

  const fetchTasksPage = useCallback(async (page: number, append: boolean) => {
    const query = batchTasksListSearchParams(page).toString();
    const data = await callApiEnvelope(`${batchPaths.tasks}?${query}`, {
      method: "GET",
    });
    const rows = normalizeCustomerBatchTasks(extractTaskListPayload(data));
    const total = extractTaskListTotal(data);

    setTaskList((prev) => {
      const merged = append ? [...prev, ...rows] : rows;
      setHasMore(batchTasksHasMore(merged.length, total, rows.length));
      return merged;
    });
    nextApiPageRef.current = page + 1;
  }, []);

  const loadInitialTasks = useCallback(async () => {
    setLoading(true);
    setState(null);
    setSearch("");
    nextApiPageRef.current = 1;
    try {
      await fetchTasksPage(1, false);
    } catch (err) {
      setTaskList([]);
      setFilterTask([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [fetchTasksPage]);

  const getTaskData = useCallback(async () => {
    await loadInitialTasks();
  }, [loadInitialTasks]);

  const loadMoreTasks = useCallback(async () => {
    if (loading || loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      await fetchTasksPage(nextApiPageRef.current, true);
    } catch (err) {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [fetchTasksPage, hasMore, loading, loadingMore]);

  useEffect(() => {
    loadInitialTasks();
  }, [loadInitialTasks]);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || !hasMore || loading || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMoreTasks();
        }
      },
      { rootMargin: "240px" }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, loadMoreTasks, loading, loadingMore]);

  const resetFiltersAndSearch = useCallback(() => {
    setState(null);
    setPendingFilters(null);
    setSearch("");
    setCustomDurationPlaceholder("");
    setResetProductDropdown((prev) => !prev);
    setResetStatusDropdown((prev) => !prev);
    loadInitialTasks();
  }, [loadInitialTasks]);

  const applyFilter = useCallback(() => {
    const filtersToApply = pendingFilters || state;

    if (filtersToApply?.fromDate || filtersToApply?.toDate) {
      if (filtersToApply.fromDate && !filtersToApply.toDate) {
        toastTimeout(true, "error", "Please Select Both Dates");
        return;
      }
      if (!filtersToApply.fromDate && filtersToApply.toDate) {
        toastTimeout(true, "error", "Please Select Both Dates");
        return;
      }
      if (new Date(filtersToApply.fromDate) > new Date(filtersToApply.toDate)) {
        toastTimeout(true, "error", "Please Select A valid Date Range");
        return;
      }
    }

    if (pendingFilters) {
      setState(pendingFilters);
    }
  }, [pendingFilters, state, toastTimeout]);

  useEffect(() => {
    setFilterTask(filterTasksClientSide(taskList, state, search));
  }, [taskList, state, search, filterTasksClientSide]);

  const cancelCustomDateRange = (val: boolean) => {
    setShowCustomDatePicker(val);
  };

  const setCustomDateRange = (date: any) => {
    setShowCustomDatePicker(false);

    // Ensure proper date formatting with leading zeros
    const toFormateDate =
      date.to.year +
      "-" +
      String(date.to.month).padStart(2, "0") +
      "-" +
      String(date.to.day).padStart(2, "0");
    const fromFromateDate =
      date.from.year +
      "-" +
      String(date.from.month).padStart(2, "0") +
      "-" +
      String(date.from.day).padStart(2, "0");

    const toDate = new Date(toFormateDate);
    const fromDate = new Date(fromFromateDate);

    setCustomDurationPlaceholder(
      `${format(fromDate, "dd/MM/yyyy")} - ${format(toDate, "dd/MM/yyyy")}`
    );

    setPendingFilters((prevData: any) => ({
      ...prevData,
      fromDate: fromDate,
      toDate: toDate,
    }));
  };

  return (
    <>
      {showCustomDatePicker && (
        <CustomDatePicker
          setShowCustomDatePicker={cancelCustomDateRange}
          setCustomDurationRange={(data: any) => {
            setCustomDateRange(data);
          }}
          maxDateDifference={90}
        />
      )}
      <div className="container-fluid p-0">
        <div className="p-4 mx-2">
          <section className="d-flex gap-4 justify-content-end align-items-center mb-4">
            <div className="d-flex justify-content-end align-items-end gap-2">
              <StyledButton
                type="submit"
                onClick={() => {
                  setAddBatch(true);
                }}
                id="batch-service-add-batch"
                style={{
                  ...buttonStyles.primary.normal,
                  opacity:
                    isBatchTypesLoading || batchTypes.length === 0 ? 0.6 : 1,
                }}
                hoverStyle={{ ...buttonStyles.primary.hover }}
                disabled={isBatchTypesLoading || batchTypes.length === 0}
              >
                {isBatchTypesLoading ? "Loading..." : "Add Batch"}
              </StyledButton>
              <div
                id="batch-sevice-get-task"
                className="d-flex align-items-center"
                onClick={getTaskData}
              >
                <div className={styles.refreshContainer}>
                  <RefreshSvg></RefreshSvg>
                </div>
              </div>

              {!isBatchTypesLoading && batchTypes.length === 0 && (
                <div className="text-muted ml-3">
                  Please subscribe to products first to add batches.
                </div>
              )}
            </div>

            <div
              className={`${styles.trackTaskFilterDiv} d-flex justify-content-end align-items-center`}
            >
              <div className={styles.headingStyle}>Filter By</div>
              <div style={{ cursor: "pointer" }}>
                <div
                  id="batch-service-filter-by"
                  onClick={() => setShowFilter(!showFilter)}
                >
                  {showFilter ? <ArrowUpSVG /> : <ArrowDownSVG />}
                </div>
              </div>
            </div>
          </section>

          {!showFilter && <hr />}
          {showFilter && (
            <div className={styles.filterBorder}>
              <div className="d-flex">
                <div className={styles.dropdown2}>
                  <div
                    className={styles.formLabel}
                    style={{ marginBottom: "8px" }}
                  >
                    Product Name
                  </div>
                  <div>
                    <InputDropdown
                      id="batch-service-product-name"
                      placeholder={
                        isBatchTypesLoading
                          ? "Loading products..."
                          : "Product Name"
                      }
                      searchable={true}
                      borderRadius={"true"}
                      no_shadow={"true"}
                      optionsArray={
                        isBatchTypesLoading
                          ? []
                          : batchTypes.map((item: any) => item?.name)
                      }
                      click={(key: string) => {
                        const option: any = batchTypes.filter(
                          (item: any) => item?.name === key
                        );
                        setPendingFilters((data: any) => ({
                          ...data,
                          batchType: option[0]?.value || "",
                          name: option[0]?.name || "",
                        }));
                      }}
                      default_value={state?.name || ""}
                      whiteBackgroundcolor={true}
                      disabled={isBatchTypesLoading}
                      resetValue={resetProductDropdown}
                    />
                  </div>
                </div>

                <div className="col-md-4 p-4">
                  <div
                    className={styles.formLabel}
                    style={{ marginBottom: "8px" }}
                  >
                    Select Duration
                  </div>

                  <Input
                    id={IdsGenerator({
                      prefix: "batch-service",
                      id: "select",
                      suffix: "duration",
                    })}
                    type="text"
                    clear={false}
                    style={{
                      width: "100%",
                      borderRadius: "8px",
                      padding: "12px",
                      fontSize: "12px",
                      backgroundColor: "#fff",
                      border: "1px solid #e8e8e8",
                      fontWeight: 100,
                      color: SECONDARYCOLOR,
                      outline: "none",
                    }}
                    // labelname="Select Duration"
                    placeholder="Select Duration"
                    value={customDurationPlaceholder || ""}
                    max={new Date().toISOString().split("T")[0]}
                    onClick={() => setShowCustomDatePicker(true)}
                    autoComplete={"off"}
                    onChange={() => {}}
                  />
                </div>

                <div className={styles.dropdown2}>
                  <div
                    className={styles.formLabel}
                    style={{ marginBottom: "8px" }}
                  >
                    Status
                  </div>
                  <InputDropdown
                    id="batch-service-status"
                    no_shadow="true"
                    placeholder="Status"
                    // labelname="Status"
                    isValid={true}
                    optionsArray={dropdownData.map(
                      (category: any) => category.label
                    )}
                    click={(value: any) => {
                      const selectedValue = dropdownData.find(
                        (category: any) => category.label === value
                      );
                      setPendingFilters((data: any) => ({
                        ...data,
                        value: selectedValue?.value || "",
                        label: selectedValue?.label || "",
                      }));
                    }}
                    className={styles.inputDropdown}
                    default_value={state?.label || ""}
                    resetValue={resetStatusDropdown}
                  />
                </div>
              </div>
              <div className="d-flex justify-content-end align-content-end align-items-center gap-4 mb-4 px-4">
                <div>
                  <StyledButton
                    style={buttonStyles.cancel.normal}
                    hoverStyle={buttonStyles.cancel.hover}
                    id="batch-service-clear-all"
                    onClick={resetFiltersAndSearch}
                  >
                    Clear all
                  </StyledButton>
                </div>
                <StyledButton
                  style={buttonStyles.primary.normal}
                  hoverStyle={buttonStyles.primary.hover}
                  id="batch-service-apply-filter"
                  onClick={applyFilter}
                >
                  Apply Filter
                </StyledButton>
              </div>
            </div>
          )}

          {loading ? (
            <div
              style={{ height: "100vh" }}
              className="d-flex align-items-center justify-content-center"
            >
              <Loading loadingColor={ACCENTCOLOR} width="14px" height="14px" />
            </div>
          ) : (
            <div>
              <TrackTask
                taskList={
                  filterTask.length === taskList.length ? taskList : filterTask
                }
                getTaskData={getTaskData}
              />
              {hasMore && !search && !state ? (
                <div
                  ref={loadMoreRef}
                  className="d-flex w-100 justify-content-center py-4"
                >
                  {loadingMore ? (
                    <Loading
                      loadingColor={ACCENTCOLOR}
                      width="14px"
                      height="14px"
                    />
                  ) : null}
                </div>
              ) : null}
            </div>
          )}
        </div>

        <AddBatchModal
          show={addBatch}
          setModalShow={setAddBatch}
          batchTypes={batchTypes}
          getTaskData={getTaskData}
          isBatchTypesLoading={isBatchTypesLoading}
          key={addBatch ? "add-batch-modal-open" : "add-batch-modal-closed"}
        />

        {downTime && (
          <WarningBar>
            <p className="m-0 p-0">
              Batch service is down for maintenance - Apologies for the
              inconvenience. We will be soon back up.
            </p>
          </WarningBar>
        )}
      </div>
    </>
  );
};

export default BatchService;
